import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { initialize, change, touch, stopSubmit } from 'redux-form';
import { getCountryCallingCode } from 'react-phone-number-input';
import { useTranslation, Trans } from 'react-i18next';
import { Spacing, Text, Icon, Visibility } from '@reservamos/elements';
import InsuranceSection from 'components/purchase/InsuranceSection';
import SeatSelectorFactory from 'components/seats/SeatSelectorFactory';
import BookingReview from 'ui/atoms/BookingReview';
import AncillariesSwitch from 'ui/atoms/AncillariesSwitch';
import LoadingScreen from 'components/LoadingScreen';
import parseQueryString from 'utils/queryString/parseQueryString';
import { getStoredPurchaser } from 'utils/storedPurchaser';
import { getBrand2ProcessorMap } from 'utils/documentMaps';
import hasAppliedDiscount from 'utils/purchase/hasAppliedDiscount';
import purchasePassengersForValidation from 'utils/purchase/purchasePassengersForValidation';
import { expectedPaymentCancellation } from 'utils/purchase/expectedPaymentCancellation';
import {
  passengers as validatePassengers,
  documentId as validateDocumentId,
} from 'utils/formValidations';
import { useInsuranceEligibility } from 'hooks/useInsuranceEligibility';
import { setError } from '@/actions';
import { releaseQuickPurchaseLoading } from '@/actions/purchase';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import useGrowthBookReady from 'hooks/useGrowthBookReady';
import {
  getQuickPurchaseInsuranceAutoDisableKey,
  getQuickPurchaseInsuranceAvailability,
  hasSelectedPassengerInsurance,
  shouldAutoDisableQuickPurchaseInsurance,
} from 'utils/quickPurchaseInsurance';
import {
  getQuickPurchaseBindingObservationKey,
  getQuickPurchaseItineraryKey,
} from 'utils/purchase/quickPurchaseInitialization';
import smartSeatSelectionResult from 'utils/purchase/smartSeatSelectionResult';
import quickPurchaseIconSrc from 'images/purchases/quick-purchase.png';
import {
  quickPurchaseBackToSearchTracker,
  quickPurchaseIncompleteDataTracker,
  quickPurchasePassengerEditTracker,
  quickPurchaseSeatsEditTracker,
  quickPurchaseViewTracker,
} from 'metrics/user-analytics/purchase';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import useQuickPurchaseCTAExperiment from 'hooks/useQuickPurchaseCTAExperiment';
import wayIsOpenTicket, { isHybridTrip } from '../../../utils/wayIsOpenTicket';
import QuickCheckoutDiscount from './QuickCheckoutDiscount';
import QuickCheckoutDiscountLossModal from './QuickCheckoutDiscountLossModal';
import QuickCheckoutTravelers from './QuickCheckoutTravelers';
import QuickCheckoutPassengersForm, {
  createPassengerSaveOnSubmit,
} from './QuickCheckoutPassengersForm';
import QuickPurchaseTimerBadge from './QuickPurchaseTimerBadge';
import useQuickCheckoutPassengersInitialValues from '../../../hooks/purchase/useQuickCheckoutPassengersInitialValues';
import useQuickCheckoutState from '../../../hooks/purchase/useQuickCheckoutState';
import usePurchaserSaveState from '../../../hooks/purchase/usePurchaserSaveState';
import useQuickCheckoutActions from '../../../hooks/purchase/useQuickCheckoutActions';
import useBuyerManuallyEdited from '../../../hooks/purchase/useBuyerManuallyEdited';
import PaymentPanel from '../PaymentPanel';
import NotWsSandboxWarning from '../NotWsSandboxWarning';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import { AgreementCheckboxFields } from '../../AgreementCheckboxFields';
import { useCdsFrequentPassenger } from '../../../context/CdsFrequentPassenger';
import {
  getQuickCheckoutAgreementPolicy,
  selectQuickCheckoutAgreementAccount,
} from '../../../utils/purchase/quickCheckoutAgreements';

/**
 * Returns true when a form value is missing (null, empty, or whitespace-only).
 * @param {string} value - The value to check.
 * @returns {boolean} Whether the value is blank.
 */
const isBlankValue = (value) => !value || String(value).trim() === '';

/**
 * QuickCheckout component.
 * Render a streamlined checkout screen for Quick Purchase. On mount, completes the
 * purchase orchestration (fetch details, auto-select seats, save passengers) while
 * showing a loading screen. Once ready, displays read-only trip summary, seats,
 * passengers, and payment form.
 * @param {object} props - history, location, goToSearch, orchestrationParams, isModal, onClose.
 * @param {object} props.history - React Router history object.
 * @param {object} props.location - React Router location object.
 * @param {Function} props.goToSearch - Navigate back to search/schedules page.
 * @param {object} [props.orchestrationParams] - Optional orchestration parameters (passengersParam,
 * selectedSeatNumbers).
 * @param {Function} props.onOrchestrationChange - Sync orchestration loading state with Purchase sidebar.
 * @param {Function} [props.onCouponAvailabilityChange] - Report whether the coupon input may be offered, so the sidebar's copy obeys the same states.
 * @param {boolean} [props.isModal] - Render QuickCheckout in modal mode.
 * @param {Function} [props.onClose] - Callback to close modal mode.
 * @param {number} [props.timerRemainingTime] - Remaining purchase time in seconds.
 */
const QuickCheckout = ({
  history,
  location,
  goToSearch,
  orchestrationParams,
  onOrchestrationChange,
  onCouponAvailabilityChange,
  isModal,
  onClose,
  timerRemainingTime,
}) => {
  const { t } = useTranslation(['purchase', 'insurance', 'payment']);
  const quickPurchaseTitle = useQuickPurchaseCTAExperiment();
  const [isOrchestrating, setIsOrchestrating] = useState(true);
  const [orchestrationError, setOrchestrationError] = useState(null);
  const [notWsSandboxWarningDismissed, setNotWsSandboxWarningDismissed] = useState(false);
  const [isEditingPassengers, setIsEditingPassengers] = useState(false);
  // A passenger field the backend refused during orchestration, held until the inline form is up.
  const [recoverableFieldError, setRecoverableFieldError] = useState(null);
  // When frequent/stored passenger or buyer data is missing tenant-required fields (e.g. an empty
  // document id), we default the affected form(s) to open so the user is forced through validation
  // before paying (CON-1076). Computed once after orchestration; a later manual toggle wins.
  // null means the one-shot incomplete-data evaluation has not finished yet. Keep the payment
  // section unmounted until this becomes a boolean so PurchaserForm reads the correct initial value.
  const [buyerFormDefaultExpanded, setBuyerFormDefaultExpanded] = useState(null);
  const forcedIncompleteFormsRef = useRef(false);
  // Inline seat editing: swaps the QuickCheckout body for the seat selector without navigating.
  const [isEditingSeats, setIsEditingSeats] = useState(false);
  // The edit the buyer asked for while a discount was applied, held until they confirm losing it.
  const [pendingEdit, setPendingEdit] = useState(null);
  const skipDiscountWarningRef = useRef(false);
  // Snapshot of the seats held when entering edit, to restore them if the user cancels without
  // re-locking (the seat selector mutates purchase.departs.selectedSeats on every tap).
  const originalSeatsRef = useRef(null);
  // Snapshot of the passenger/purchaser redux-form values captured when entering the inline seat
  // editor. Those edits live only in redux-form (not persisted until "Finalizar compra"), so both
  // the summary unmount (destroyOnUnmount) and the seat relock's getPurchase (which mergeDeeps the
  // pre-edit backend purchase) would wipe them. Restored on returning from the editor (PRO2-8).
  const preservedFormsRef = useRef(null);
  const passengersOnSuccessRef = useRef(null);
  const autoDisableInsuranceAttemptRef = useRef(null);
  const dispatch = useDispatch();
  const { userIsLoggedInWithAnyLoyaltyProgram } = useLoyaltyPrograms();
  const quickPurchaseLoyaltyExitRef = useRef(false);
  const orchestrationStartedKeyRef = useRef(null);
  const orchestrationInitializationRef = useRef(null);
  const mountedRef = useRef(false);
  // Controlled rollout: when off, the edit-seats button redirects to the seats page
  // (previous behavior); when on, it swaps the seat selector in place.
  const seatEditInPlaceEnabled = useExperimentalFeature('QUICK_PURCHASE_SEAT_EDIT_ENABLED');
  const growthBookReady = useGrowthBookReady();
  const smartSeatSelectionEnabled =
    useExperimentalFeature('SMART_SEAT_SELECTION_FOR_QUICK_PURCHASE_ENABLED') === true;

  const {
    purchase,
    payment,
    env,
    features,
    isUpdating,
    fetchingPayment,
    paymentId,
    cardErrorOccured,
    cardValues,
    cardCheckoutMode,
    paymentEngine,
    selectedPaymentMethod,
    purchaser,
    purchaserFormValues,
    legalTerms,
    insuranceMessage,
    insuranceAmount,
    insuranceAmountWithTaxes,
    insuranceAmountIsUnitPrice,
    carbonOffset,
    wantsCarbonOffset,
    updatingCarbonOffset,
  } = useQuickCheckoutState();
  const cdsSession = useCdsFrequentPassenger();
  const agreementAccount = useSelector(selectQuickCheckoutAgreementAccount, shallowEqual);
  const quickCheckoutAgreementPolicy = getQuickCheckoutAgreementPolicy({
    features,
    brand: env.brand,
    cdsSession,
    ...agreementAccount,
  });

  // Whether the buyer form still equals what the last successful purchaser save sent (PRO2-146).
  // A one-way "already saved" flag used to drop every edit made after the first save. Owned here,
  // not in usePaymentSection, so recording a save re-renders QuickCheckout and re-reads the latest
  // store state (isUpdating, updatingInsurance, ...). Without that, PaymentPanel re-renders alone
  // with stale props after the purchaser save and the pending-payment effect resumes payment
  // before the latest state lands.
  const { purchaserSubmitted, markPurchaserSaved } = usePurchaserSaveState(purchaserFormValues);

  // Gates the warning shown before an edit that would destroy an applied discount code. An apply
  // still in flight counts: `discountAmount` is only written when it lands, so reading it alone
  // leaves the whole request window unguarded.
  const discountApplied = hasAppliedDiscount(purchase) || Boolean(purchase.applyingDiscount);

  const actions = useQuickCheckoutActions({ history });
  const passengersInitialValues = useQuickCheckoutPassengersInitialValues();
  const buyerManuallyEdited = useBuyerManuallyEdited();

  const {
    token,
    departs,
    returns,
    passengers,
    total,
    qualifiesForInsurance,
    wantsInsurance,
    updatingInsurance,
    storeReferenceIssued,
  } = purchase;

  // Flattened on purpose. Between the seat re-lock and the getPurchase that follows it,
  // `savePassengersWithTickets` stores `polls.map(({ payload }) => payload.departs.tickets)`, so the
  // way's `tickets` is an array of arrays until the parsed purchase lands. Without this the
  // seat-amount lookup finds no match in that window and the price it just made the buyer edit a
  // seat for blinks out. On the already-parsed purchase flattening is a no-op.
  const departureTickets = useMemo(() => (departs.tickets || []).flat(), [departs.tickets]);
  const returnTickets = useMemo(() => (returns.tickets || []).flat(), [returns.tickets]);

  const routeQuery = parseQueryString(location?.search || '');
  const searchPassengers = orchestrationParams?.passengersParam || routeQuery.passengers || 'A1';
  const selectedSeatNumbers =
    orchestrationParams?.selectedSeatNumbers ||
    (routeQuery.seats ? routeQuery.seats.split(',') : null);
  const selectedSeatReferences =
    orchestrationParams?.selectedSeatReferences || location?.state?.selectedSeatReferences || null;
  const insuranceEligibility = useInsuranceEligibility({ purchaser, purchase });
  const itineraryKey = getQuickPurchaseItineraryKey(purchase);
  const bindingObservationKey = getQuickPurchaseBindingObservationKey(purchase);
  const orchestrationStartKey = JSON.stringify([token, itineraryKey]);
  const hasPreconfiguredPurchase = passengers?.length > 0 && Boolean(purchase.hasTickets);
  const { onCompleteQuickPurchase, transitionTo } = actions;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onOrchestrationChange?.(isOrchestrating);

    return () => {
      onOrchestrationChange?.(false);
    };
  }, [isOrchestrating, onOrchestrationChange]);

  useEffect(() => {
    if (!onCompleteQuickPurchase) return;
    const previousInitialization = orchestrationInitializationRef.current;
    const previousStatus = previousInitialization?.getStatus?.();
    if (
      orchestrationStartedKeyRef.current === orchestrationStartKey &&
      previousStatus !== 'invalidated'
    ) {
      return;
    }
    if (previousStatus === 'invalidated') {
      if (!previousInitialization.canReleaseLoading?.()) return;

      // Dispose A while it is still the latest invalidated generation. begin(B) intentionally
      // changes A to replaced, at which point A must no longer be able to clear loading. Reducer
      // owner matching makes this release harmless if another request already retagged a field.
      // This applies to both same-key binding recovery and a changed token/route start key.
      const attemptLoadingOwnerId = smartSeatSelectionResult.invalidateLoadingAttempt({
        handle: previousInitialization,
        token: previousInitialization.token,
      });
      if (attemptLoadingOwnerId) {
        dispatch(releaseQuickPurchaseLoading(attemptLoadingOwnerId));
      }
      dispatch(releaseQuickPurchaseLoading(previousInitialization.loadingOwnerId));
    }

    const retainedInitialization = smartSeatSelectionResult.getInitialization({
      token,
      itineraryKey,
    });

    // A full reload has no in-memory receipt lifecycle to reconstruct. A component remount in the
    // same runtime does: attach to its existing promise even if passengers/tickets already landed.
    if (hasPreconfiguredPurchase && !retainedInitialization) {
      orchestrationStartedKeyRef.current = orchestrationStartKey;
      setIsOrchestrating(false);
      quickPurchaseViewTracker();
      return;
    }

    if (!growthBookReady && !retainedInitialization) return;

    orchestrationStartedKeyRef.current = orchestrationStartKey;
    const attempt =
      retainedInitialization ||
      smartSeatSelectionResult.beginOrReuseInitialization({
        token,
        itineraryKey,
        operation: (initialization) =>
          onCompleteQuickPurchase({
            passengers: searchPassengers,
            selectedSeatNumbers,
            selectedSeatReferences,
            smartSeatSelectionEnabled,
            initialization,
          }),
      });
    if (!attempt) return;
    const { handle: initialization, promise } = attempt;
    orchestrationInitializationRef.current = initialization;
    setIsOrchestrating(true);
    setOrchestrationError(null);

    promise
      .then((result) => {
        if (!mountedRef.current || !initialization.isCurrent() || result?.superseded) return;
        setIsOrchestrating(false);

        if (result.bindingRejected) {
          dispatch(releaseQuickPurchaseLoading(initialization.loadingOwnerId));
          transitionTo(`/purchase/${result.token || token}/seats/departure`);
          return;
        }

        if (result.success) {
          quickPurchaseViewTracker();
        }

        if (!result.success) {
          errorMonitoring.notify({
            error: new Error(`Quick checkout orchestration failed: ${result.error}`),
            info: { from: 'QuickCheckout:orchestration', error: result.error, token },
          });
          // A refused passenger field is not terminal, and it is not a reason to leave Quick
          // Purchase either: the buyer only has to correct one document on a purchase that is
          // otherwise built. Open the passengers form that already lives on this screen — the same
          // remedy CON-1076 applies to a passenger whose saved data is incomplete — instead of
          // setting the error that replaces the whole page. The re-dispatch waits for the effect
          // below, because the form clears the error state as it initialises.
          if (result.fieldError) {
            setRecoverableFieldError(result.fieldError);
            setIsEditingPassengers(true);
            return;
          }

          setOrchestrationError(result.error);

          if (
            result.error === 'NOT_ENOUGH_SEATS_DEPARTURE' ||
            result.error === 'NOT_ENOUGH_SEATS_RETURN'
          ) {
            transitionTo(`/purchase/${result.token || token}/seats/departure`);
          }
        }
      })
      .catch((error) => {
        if (!mountedRef.current || !initialization.isCurrent()) return;
        setIsOrchestrating(false);
        setOrchestrationError(error.message);
        errorMonitoring.notify({
          error,
          info: { from: 'QuickCheckout:orchestration', token },
        });
      });
    // Input arrays are snapshotted only when the primitive token/itinerary start key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    growthBookReady,
    hasPreconfiguredPurchase,
    bindingObservationKey,
    dispatch,
    itineraryKey,
    onCompleteQuickPurchase,
    orchestrationStartKey,
    smartSeatSelectionEnabled,
    token,
    transitionTo,
  ]);

  // Put the refused document back on its own field, now that the form exists to hold it.
  //
  // `savePassengersForQuickPurchase` already dispatched this error, but redux-form's INITIALIZE
  // starts from an empty form state and restores only `warning`, `syncWarnings`, `error`,
  // `syncErrors` and `registeredFields` — `submitErrors` and every `touched` flag are dropped. The
  // form mounts after the failed save, so that first dispatch is always erased. This effect runs
  // after the children have mounted and initialised, which is the earliest point the error can
  // survive. The banner raised during the save is untouched and stays on this same screen.
  useEffect(() => {
    if (!isEditingPassengers || !recoverableFieldError) return;

    dispatch(touch('passengers', ...recoverableFieldError.fieldsToTouch));
    dispatch(stopSubmit('passengers', recoverableFieldError.errors));
    setRecoverableFieldError(null);
  }, [isEditingPassengers, recoverableFieldError, dispatch]);

  // Fetch Yuno session data after orchestration completes
  useEffect(() => {
    if (isOrchestrating || orchestrationError) return;
    if (!env.yuno?.enabled || !total || total <= 0) return;

    const { api: { purchaseUrl } = {} } = env;
    actions.onFetchYunoData({ purchaseUrl, purchaseToken: token, amount: total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrchestrating]);

  // Pre-fill purchaser form: first passenger data takes priority, localStorage as fallback
  useEffect(() => {
    if (isOrchestrating || orchestrationError) return;

    const firstPassenger = passengers?.[0] || {};
    const storedPurchaser = getStoredPurchaser();
    const phoneCountry = storedPurchaser?.phoneCountry || env.lang?.default?.substring(3) || 'MX';
    let phoneCode = '';
    try {
      phoneCode = getCountryCallingCode(phoneCountry);
    } catch (e) {
      // Invalid country code, leave empty
    }

    // After orchestration the backend returns document_type as the processor value (e.g. "PP").
    // We need to reverse-map it back to the brand value (e.g. "PAS") so the purchaser form
    // initializes with a value that matches the DocumentTypeField dropdown options.
    const brand2ProcessorMap = getBrand2ProcessorMap();
    const processor2BrandMap = Object.fromEntries(
      Object.entries(brand2ProcessorMap).map(([brand, proc]) => [proc, brand]),
    );
    const passengerDocType = firstPassenger.documentType;
    const brandDocType =
      processor2BrandMap[passengerDocType] ||
      passengerDocType ||
      storedPurchaser?.documentType ||
      'CC';

    const purchaserInitialValues = {
      purchaserFirstName: firstPassenger.firstName || storedPurchaser?.purchaserFirstName || '',
      purchaserLastName: firstPassenger.lastName || storedPurchaser?.purchaserLastName || '',
      purchaserSecondLastName:
        firstPassenger.secondLastName || storedPurchaser?.purchaserSecondLastName || '',
      email: firstPassenger.email || storedPurchaser?.email || '',
      phone: storedPurchaser?.phone || '',
      phoneCountry,
      phoneCode,
      documentType: brandDocType,
      documentId: firstPassenger.documentId || storedPurchaser?.documentId || '',
      legalTerms: false,
      promotionalTerms:
        features.POLICIES_DISCLAIMER_LOCATION === 'CHECKOUT' &&
        Boolean(features.PROMOTIONAL_TERMS_BY_DEFAULT),
      cdsFrequentPassengerOptIn: purchase.meta?.frequent_passenger_enrollment === 'true',
    };

    dispatch(initialize('purchaser', purchaserInitialValues));

    // Default the buyer/passenger forms to open when their tenant-required data is incomplete, so
    // the existing validators run and block payment before an empty document reaches the backend
    // (CON-1076). One-shot: never override a subsequent manual toggle.
    if (!forcedIncompleteFormsRef.current) {
      forcedIncompleteFormsRef.current = true;

      // Passengers: reuse the exact form-level validator the mounted passengers form uses. It reads
      // the tenant flags from the store, so it enforces only the fields this client requires. Its
      // return is an errors array (one entry per passenger) — collect each missing field key.
      // `email` is special: the shared validator hard-requires it for passenger 0 regardless of
      // tenant, but only clients that actually collect a passenger email (PASSENGERS_EXTRA_FIELDS /
      // SHOW_PASSENGERS_EMAIL_FIELD, matching PersonPassengersForm) should let a missing email force
      // this form open — otherwise it fires for tenants that don't ask for it (CON-1076 feedback).
      const collectsPassengerEmail = Boolean(
        features.PASSENGERS_EXTRA_FIELDS || features.SHOW_PASSENGERS_EMAIL_FIELD,
      );
      const passengerErrors = validatePassengers(purchasePassengersForValidation(passengers));
      const passengerMissingFields = [];
      if (Array.isArray(passengerErrors)) {
        passengerErrors.forEach((error, index) => {
          if (error && typeof error === 'object') {
            Object.keys(error).forEach((field) => {
              if (field === 'email' && !collectsPassengerEmail) return;
              if (error[field]) passengerMissingFields.push(`passenger_${index + 1}.${field}`);
            });
          }
        });
      }
      if (passengerMissingFields.length > 0) setIsEditingPassengers(true);

      // Buyer: only the identity/document fields are hidden while the purchaser form is collapsed
      // (email/phone stay registered and validated), so those are the only fields that can slip
      // through. Force-expand when any of them is missing/invalid.
      const usesBusinessName =
        Boolean(features.ACCEPT_PURCHASER_BUSINESS_NAME) &&
        purchaserInitialValues.documentType === 'NIT';
      const buyerMissingFields = [];
      if (isBlankValue(purchaserInitialValues.purchaserFirstName)) {
        buyerMissingFields.push('buyer.purchaserFirstName');
      }
      if (!usesBusinessName && isBlankValue(purchaserInitialValues.purchaserLastName)) {
        buyerMissingFields.push('buyer.purchaserLastName');
      }
      if (features.IDENTIFICATION_DOCUMENT_FOR_PURCHASER) {
        if (isBlankValue(purchaserInitialValues.documentType)) {
          buyerMissingFields.push('buyer.documentType');
        }
        if (
          validateDocumentId(purchaserInitialValues.documentId, {
            documentType: purchaserInitialValues.documentType,
          })
        ) {
          buyerMissingFields.push('buyer.documentId');
        }
      }
      setBuyerFormDefaultExpanded(buyerMissingFields.length > 0);

      // Emit a single analytics event listing every missing field so we can size and diagnose the
      // incomplete-frequent-passenger data issue this fix guards against (CON-1076).
      const missingFields = [...passengerMissingFields, ...buyerMissingFields];
      if (missingFields.length > 0) {
        quickPurchaseIncompleteDataTracker(missingFields);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrchestrating]);

  // Redirect to PaymentProcessor after payment confirmation completes
  const prevFetchingPayment = useRef(false);
  useEffect(() => {
    if (prevFetchingPayment.current && !fetchingPayment && paymentId) {
      actions.transitionTo(`/payment/${token}/${paymentId}`);
    }
    prevFetchingPayment.current = fetchingPayment;
  }, [fetchingPayment, paymentId, token, actions]);

  const passengerInsuranceAvailability = useMemo(
    () => getQuickPurchaseInsuranceAvailability({ features, purchase }),
    [features, purchase],
  );
  const usesPassengerInsurance = Boolean(features.PASSENGERS_INSURANCE_ENABLED);
  const isHybrid = isHybridTrip(departs, returns);
  const showMessageNotInsuranceOpenTicket = isHybrid && !features.OPENT_TICKET_INSURANCE_ENABLED;
  const enablesCheckoutInsurance =
    !usesPassengerInsurance && qualifiesForInsurance && !showMessageNotInsuranceOpenTicket;
  const enablesInsurance = usesPassengerInsurance
    ? passengerInsuranceAvailability.enabled
    : enablesCheckoutInsurance;
  const selectedInsurance = usesPassengerInsurance
    ? hasSelectedPassengerInsurance(purchase)
    : Boolean(wantsInsurance);
  const insuranceDisabled = Boolean(insuranceEligibility.hasIneligibleDoc);
  const autoDisableInsuranceAttemptKey = getQuickPurchaseInsuranceAutoDisableKey({
    token,
    usesPassengerInsurance,
    passengerDocs: insuranceEligibility.passengerDocs,
  });

  const handleInsuranceToggle = useCallback(
    (event) => {
      const wants = event.target.checked;

      if (usesPassengerInsurance) {
        actions.onTogglePassengerInsurance(token, wants);
        return;
      }

      actions.onToggleWantsInsurance(token, wants);
    },
    [actions, token, usesPassengerInsurance],
  );

  useEffect(() => {
    if (autoDisableInsuranceAttemptRef.current !== autoDisableInsuranceAttemptKey) {
      autoDisableInsuranceAttemptRef.current = null;
    }

    if (!enablesInsurance || !selectedInsurance || !insuranceDisabled) {
      autoDisableInsuranceAttemptRef.current = null;
    }
  }, [autoDisableInsuranceAttemptKey, enablesInsurance, insuranceDisabled, selectedInsurance]);

  useEffect(() => {
    if (
      !shouldAutoDisableQuickPurchaseInsurance({
        isOrchestrating,
        enablesInsurance,
        selectedInsurance,
        insuranceDisabled,
        updatingInsurance,
        attemptedKey: autoDisableInsuranceAttemptRef.current,
        attemptKey: autoDisableInsuranceAttemptKey,
      })
    ) {
      return;
    }

    autoDisableInsuranceAttemptRef.current = autoDisableInsuranceAttemptKey;

    if (usesPassengerInsurance) {
      actions.onTogglePassengerInsurance(token, false);
      return;
    }

    actions.onToggleWantsInsurance(token, false);
  }, [
    actions,
    enablesInsurance,
    insuranceDisabled,
    isOrchestrating,
    autoDisableInsuranceAttemptKey,
    selectedInsurance,
    token,
    updatingInsurance,
    usesPassengerInsurance,
  ]);

  // Once a payment reference has already been generated (e.g. store/OXXO payments, where
  // the reference is issued before the purchase completes), seats/passengers must not be
  // editable anymore — mirrors the redirect guard in Purchase.js for the non-quick flow.
  // Store references stay blocking regardless of status (failed/canceled included): unlike
  // card/transfer attempts, generating a new one is a backend-side concern that a client-side
  // retry should not trigger, so once one exists for this purchase, editing stays locked.
  const hasBlockingPaymentAttempt = Boolean(
    paymentId &&
      (payment.paymentType === 'store' ||
        (payment.status && !['failed', 'canceled'].includes(payment.status))),
  );

  // A coupon moves the total, so it must not be offered in states where the buyer can no longer
  // change anything else: an issued store reference or a blocking payment attempt both refuse every
  // edit outright, and the inline seat editor ends in a re-lock that would overwrite the discount.
  const couponOffered = !hasBlockingPaymentAttempt && !storeReferenceIssued && !isEditingSeats;

  const handleSeatsEdit = useCallback(
    (way) => {
      if (hasBlockingPaymentAttempt) {
        actions.transitionTo(`/payment/${token}/${paymentId}`);
        return;
      }
      // AM-1686: a store reference can exist even when the transient payment state was cleared
      // (e.g. a failed offline_lock leaves state.payment empty). The purchase-level flag is the
      // reliable signal, so block the edit up front and show the reason instead of navigating.
      if (storeReferenceIssued) {
        dispatch(setError(204, 'store_reference_edit_blocked', 'error', false));
        return;
      }
      if (discountApplied && !skipDiscountWarningRef.current) {
        setPendingEdit({ kind: 'seats', way });
        return;
      }
      quickPurchaseSeatsEditTracker();
      const canceledLoadingOwnerId = smartSeatSelectionResult.invalidateLoadingAttempt({ token });
      if (canceledLoadingOwnerId) {
        dispatch(releaseQuickPurchaseLoading(canceledLoadingOwnerId));
      }
      smartSeatSelectionResult.invalidateSelection({ token });

      if (!seatEditInPlaceEnabled) {
        // Previous behavior: navigate to the standard seat selection page.
        actions.onClearSelectedSeats();
        actions.transitionTo(`/purchase/${token}/seats/${way}`);
        return;
      }

      // Snapshot the in-progress purchaser form edits before the summary unmounts, so they can be
      // restored on return (PRO2-8). The passengers form no longer needs identity snapshotting: it
      // keeps dirty edits across the seat-editor unmount and store reverts (destroyOnUnmount:false
      // + keepDirtyOnReinitialize:true). Seats are synced separately via change() on return.
      preservedFormsRef.current = {
        purchaser: { ...purchaserFormValues, legalTerms },
      };

      // Keep a snapshot so cancel can restore the locked tickets, but start the editor empty
      // to match the normal purchase flow when users re-enter seat selection.
      originalSeatsRef.current = departs.selectedSeats || [];
      actions.onSelectSeats('departs', []);
      setIsEditingSeats(true);
    },
    [
      seatEditInPlaceEnabled,
      actions,
      token,
      departs.selectedSeats,
      purchaserFormValues,
      legalTerms,
      hasBlockingPaymentAttempt,
      paymentId,
      storeReferenceIssued,
      dispatch,
      discountApplied,
    ],
  );

  // Restore the purchaser form edits captured in handleSeatsEdit once the inline seat editor closes
  // (confirm or cancel). Runs after the summary block has remounted, so this initialize wins over
  // the form's own mount-time initialize from the (reverted) store.
  // Also sync passengers[i].seats from store-derived initialValues: seats are not registered Fields,
  // so keepDirtyOnReinitialize does not refresh them (header would stay stale). Only change seats —
  // never initialize('passengers') — so dirty identity edits stay dirty across later getPurchase
  // reverts (PRO2-8).
  useEffect(() => {
    if (isEditingSeats) return;
    const preserved = preservedFormsRef.current;
    if (!preserved) return;
    preservedFormsRef.current = null;
    if (preserved.purchaser) dispatch(initialize('purchaser', preserved.purchaser));
    const freshPassengers = passengersInitialValues?.passengers || [];
    freshPassengers.forEach((passenger, index) => {
      if (passenger?.seats) {
        dispatch(change('passengers', `passengers[${index}].seats`, passenger.seats));
      }
    });
  }, [isEditingSeats, dispatch, passengersInitialValues]);

  const handleSeatsConfirmed = useCallback(() => {
    // Only fire the re-lock. The success/failure transition is driven by the isLockingTickets
    // effect below, not by this call's result: lockTickets swallows errors and resolves
    // regardless, so an awaited result cannot tell success from failure.
    actions.onUpdateQuickPurchaseSeats(token, departs.selectedSeats || [], {
      savePreference: true,
    });
  }, [actions, departs.selectedSeats, token]);

  // Drive the inline seat editor's outcome from the re-lock's Redux state instead of the thunk's
  // promise. When isLockingTickets falls back to false after a save, close the editor (back to the
  // summary) only if no ticketsError was recorded; otherwise stay in the editor so the user retries
  // with the refreshed availability (the error toast + bus refresh already happened in the thunk).
  const wasLockingRef = useRef(false);
  useEffect(() => {
    const isLocking = purchase.isLockingTickets;
    if (isEditingSeats && wasLockingRef.current && !isLocking) {
      if (!purchase.ticketsErrorType) {
        originalSeatsRef.current = null;
        setIsEditingSeats(false);
      }
    }
    wasLockingRef.current = isLocking;
  }, [purchase.isLockingTickets, purchase.ticketsErrorType, isEditingSeats]);

  const handleSeatsEditCancel = useCallback(() => {
    if (originalSeatsRef.current) {
      actions.onSelectSeats('departs', originalSeatsRef.current);
      originalSeatsRef.current = null;
    }
    setIsEditingSeats(false);
  }, [actions]);

  const handlePassengersEdit = useCallback(() => {
    if (hasBlockingPaymentAttempt) {
      actions.transitionTo(`/payment/${token}/${paymentId}`);
      return;
    }
    // AM-1686: block the edit up front when a store reference already exists (reliable
    // purchase-level signal), showing the reason instead of opening the passengers form.
    if (storeReferenceIssued) {
      dispatch(setError(204, 'store_reference_edit_blocked', 'error', false));
      return;
    }
    quickPurchasePassengerEditTracker();
    setIsEditingPassengers(true);
  }, [hasBlockingPaymentAttempt, actions, token, paymentId, storeReferenceIssued, dispatch]);

  // Submits the passengers form and parks the payment promise on its outcome.
  const submitPassengersForPayment = useCallback(
    (resolve, reject) => {
      if (passengersOnSuccessRef.current) {
        passengersOnSuccessRef.current.reject(expectedPaymentCancellation('passengers_superseded'));
      }
      passengersOnSuccessRef.current = { resolve, reject };
      actions.onSubmitForm('passengers');
    },
    [actions],
  );

  // Runs the edit the buyer just confirmed. The ref suppresses the guard for that one call instead
  // of latching, so the next edit asks again.
  const handleDiscountLossConfirm = useCallback(() => {
    const pending = pendingEdit;
    setPendingEdit(null);
    if (!pending) return;

    skipDiscountWarningRef.current = true;
    try {
      handleSeatsEdit(pending.way);
    } finally {
      skipDiscountWarningRef.current = false;
    }
  }, [pendingEdit, handleSeatsEdit]);

  // Declining must settle the promise payment is waiting on. `usePaymentSection` rejects its own
  // pending params on a rejection, so the charge never starts and the button is released; leaving
  // it unsettled would strand both.
  const handleDiscountLossCancel = useCallback(() => {
    if (pendingEdit?.reject) {
      pendingEdit.reject(expectedPaymentCancellation('discount_loss_declined'));
    }
    setPendingEdit(null);
  }, [pendingEdit]);

  const handlePassengersSaved = useCallback(() => {
    // The passenger save also writes buyer data from the form (createPassengerSaveOnSubmit), so the
    // last purchaser-path snapshot no longer describes the purchase. Forget it: changing the form
    // back to that snapshot must not count as saved (PRO2-146, design D5).
    markPurchaserSaved(null);
    if (passengersOnSuccessRef.current) {
      passengersOnSuccessRef.current.resolve();
      passengersOnSuccessRef.current = null;
    }
    setIsEditingPassengers(false);
  }, [markPurchaserSaved]);

  const handlePassengersSubmitFail = useCallback(() => {
    if (passengersOnSuccessRef.current) {
      passengersOnSuccessRef.current.reject(
        expectedPaymentCancellation('passengers_validation_failed'),
      );
      passengersOnSuccessRef.current = null;
    }
    // Surface the "check the fields in red" warning below, mirroring the purchaser form's own
    // submit-fail behavior (PurchaserForm/index.js) and the regular Checkout (CON-1076).
    actions.onFormFail();
  }, [actions]);

  // Saves the passengers the pay click found open, then lets payment proceed. It asks nothing
  // first, and must not: in Quick Checkout this save can only carry identity fields — the category
  // selector is not rendered (`busCategories={[]}`), `deletePassenger` is a no-op, and the surface
  // is adults-only — so it cannot move the total, and `drop_discount_if_invalidated` (BOO-114,
  // platform-purchase) only releases a coupon whose minimum the new total no longer reaches. A
  // prompt here warned of a loss that cannot happen, and reached exactly the buyers CON-1076 forces
  // this form open for: the ones who never chose to edit anything.
  const handleBeforePaymentAsync = useCallback(
    () => new Promise((resolve, reject) => submitPassengersForPayment(resolve, reject)),
    [submitPassengersForPayment],
  );

  const handlePassengersSubmit = useCallback(
    (fields) =>
      createPassengerSaveOnSubmit(dispatch, purchase, {
        buyerManuallyEdited,
        purchaserFormValues,
      })(fields),
    [dispatch, purchase, buyerManuallyEdited, purchaserFormValues],
  );

  const handleBackToSearch = useCallback(() => {
    quickPurchaseBackToSearchTracker();
    smartSeatSelectionResult.disposeBooking({
      token,
      onLoadingOwnerDisposed: (loadingOwnerId) =>
        dispatch(releaseQuickPurchaseLoading(loadingOwnerId)),
    });

    if (isModal && onClose) {
      onClose();
      return;
    }

    goToSearch();
  }, [dispatch, goToSearch, isModal, onClose, token]);

  useEffect(() => {
    if (!userIsLoggedInWithAnyLoyaltyProgram || quickPurchaseLoyaltyExitRef.current) {
      return;
    }

    quickPurchaseLoyaltyExitRef.current = true;
    handleBackToSearch();
  }, [handleBackToSearch, userIsLoggedInWithAnyLoyaltyProgram]);

  // The sidebar renders outside this component, so it cannot see these states on its own.
  useEffect(() => {
    onCouponAvailabilityChange?.(couponOffered);
  }, [couponOffered, onCouponAvailabilityChange]);

  const handleToggleCarbonOffset = useCallback(() => {
    actions.onToggleWantsCarbonOffset(token, !wantsCarbonOffset);
  }, [actions, token, wantsCarbonOffset]);

  const enablesCarbon = Boolean(carbonOffset && carbonOffset.available);

  if (orchestrationError) {
    return (
      <div className="p-8 text-center">
        <Text size="L" color="error">
          {orchestrationError}
        </Text>
      </div>
    );
  }

  // Keep the payment section mounted while an inline passenger edit re-locks tickets.
  // Its pending payment intent must survive until passenger and purchaser saves complete.
  const formDefaultsReady = buyerFormDefaultExpanded !== null;
  const isReady =
    !isOrchestrating &&
    formDefaultsReady &&
    (!purchase.isLockingTickets || isEditingPassengers || purchase.applyingDiscount);
  const showNotWsSandboxWarning = features.NOT_WS_SANDBOX && !notWsSandboxWarningDismissed;

  return (
    <div>
      {showNotWsSandboxWarning && (
        <NotWsSandboxWarning onDismiss={() => setNotWsSandboxWarningDismissed(true)} />
      )}
      {pendingEdit && (
        <QuickCheckoutDiscountLossModal
          onConfirm={handleDiscountLossConfirm}
          onCancel={handleDiscountLossCancel}
        />
      )}
      <div className="lg:bg-white/80 lg:backdrop-blur-lg rounded-t-3xl relative z-[1] py-6 lg:px-5 -mt-[90px] lg:-mt-10">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {!isModal && (
              <button
                type="button"
                onClick={handleBackToSearch}
                className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 block lg:hidden"
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
                >
                  <Icon type="ChevronLeft" size="S" color="grayMedium" />
                </span>
              </button>
            )}
            <img src={quickPurchaseIconSrc} alt="" width={24} height={24} />
            <Text weight="bold" size="XL" className="text-3xl">
              {quickPurchaseTitle}
            </Text>
          </div>
          {isModal && isReady && <QuickPurchaseTimerBadge remainingTime={timerRemainingTime} />}
          {!isModal && (
            <button
              type="button"
              onClick={handleBackToSearch}
              className="items-center gap-3 bg-transparent border-none cursor-pointer p-0 hidden lg:flex"
            >
              <Text size="S" color="grayMedium" weight="semibold">
                {t('quick_checkout.back_to_schedules')}
              </Text>
              <span
                className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
              >
                <Icon type="ChevronLeft" size="S" color="grayMedium" />
              </span>
            </button>
          )}
        </div>

        {!isReady && !isEditingSeats && <LoadingScreen hasHeader={false} />}

        {seatEditInPlaceEnabled && isEditingSeats && (
          <Spacing vertical size="M">
            <button
              type="button"
              onClick={handleSeatsEditCancel}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 self-start"
            >
              <span
                className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
              >
                <Icon type="ChevronLeft" size="S" color="grayMedium" />
              </span>
              <Text size="S" color="grayMedium" weight="semibold">
                {t('quick_checkout.back_to_summary')}
              </Text>
            </button>
            <SeatSelectorFactory
              way="departure"
              isPurchaseView
              resultsLayout
              embedded
              isRoundTrip={false}
              onFinishSeatSelection={handleSeatsConfirmed}
              onCloseModal={handleSeatsEditCancel}
              frequentPassengersCount={0}
              seatEditMode="quickCheckout"
              requiredSeatsCount={passengers.length}
            />
          </Spacing>
        )}

        {isReady && !isEditingSeats && (
          <Spacing vertical size="L">
            <BookingReview
              alwaysShow
              showTitle={false}
              departureDate={departs.departure}
              arrivalDate={departs.arrival}
              duration={departs.duration}
              departureTerminal={departs.origin?.name}
              departureCity={departs.origin?.cityName}
              destinationTerminal={departs.destination?.name}
              destinationCity={departs.destination?.cityName}
              returnDate={returns.departure}
              passengers={passengers}
              isDepartureOpen={wayIsOpenTicket(departs)}
              isReturnOpen={wayIsOpenTicket(returns)}
              showTripDetailsButton={false}
              showPassengerCategories={false}
            />

            <QuickCheckoutTravelers
              passengers={passengers}
              departureSeats={departs.selectedSeats || []}
              returnSeats={returns.selectedSeats || []}
              departureTickets={departureTickets}
              returnTickets={returnTickets}
              onEditSeats={handleSeatsEdit}
              onEditPassengers={handlePassengersEdit}
              isEditingPassengers={isEditingPassengers}
            />

            {isEditingPassengers && (
              <QuickCheckoutPassengersForm
                initialValues={passengersInitialValues}
                onSubmit={handlePassengersSubmit}
                onPassengersSaved={handlePassengersSaved}
                onPassengersSubmitFail={handlePassengersSubmitFail}
                buyerManuallyEdited={buyerManuallyEdited}
              />
            )}

            <InsuranceSection
              enablesInsurance={enablesInsurance}
              wantsInsurance={selectedInsurance}
              hasIneligibleDoc={insuranceDisabled}
              onToggle={handleInsuranceToggle}
              isLoading={updatingInsurance}
              insuranceData={{
                message: insuranceMessage,
                price: insuranceAmount,
                singlePrice: insuranceAmountIsUnitPrice && !showMessageNotInsuranceOpenTicket,
                plusTaxes: insuranceAmountIsUnitPrice && !features.INSURANCE_ON_CHECKOUT_ONLY,
                fullPrice: insuranceAmountWithTaxes,
              }}
              t={t}
            />

            {/* One *visible* input per surface, decided by CSS rather than by mounting. On the
                route both this copy and the sidebar's stay mounted at every width and
                `hideForLargeOnly` hides this one where the sidebar shows — the same arrangement
                `Checkout.js` uses for the standard checkout. The modal mounts outside Purchase.js
                and has no sidebar, so there this copy shows unconditionally.
                Keeping both mounted is the whole point: they share the `discountCode` redux-form
                name, and `redux-form` destroys a form's state on unmount with no reference
                counting, so gating either mount on the breakpoint erases a code the buyer already
                typed the moment the viewport crosses `large`. Both halves of that were tried in
                this PR and reverted. See the "discount-code input reaches every Quick Purchase
                surface" requirement in openspec/specs/quick-purchase-discount-code/spec.md. */}
            {couponOffered &&
              (isModal ? (
                <QuickCheckoutDiscount />
              ) : (
                <Visibility type="hideForLargeOnly">
                  <QuickCheckoutDiscount />
                </Visibility>
              ))}

            {enablesCarbon && (
              <AncillariesSwitch
                isLoading={updatingCarbonOffset}
                id="carbon-footprint"
                title={carbonOffset?.title}
                icon={carbonOffset?.imageUrl}
                message={
                  <Trans components={{ strong: <strong /> }}>{carbonOffset?.description}</Trans>
                }
                linkText={t('purchase:link.see_more')}
                href={carbonOffset?.urls}
                onChange={handleToggleCarbonOffset}
                checked={wantsCarbonOffset}
                price={carbonOffset?.amount}
              />
            )}

            <PaymentPanel
              purchase={purchase}
              env={env}
              features={features}
              quickCheckoutAgreementPolicy={quickCheckoutAgreementPolicy}
              purchaserAgreementContent={
                quickCheckoutAgreementPolicy.hasAgreements ? <AgreementCheckboxFields /> : null
              }
              purchaser={purchaser}
              purchaserFormValues={purchaserFormValues}
              legalTerms={legalTerms}
              cardValues={cardValues}
              actions={actions}
              cardErrorOccured={cardErrorOccured}
              cardCheckoutMode={cardCheckoutMode}
              paymentEngine={paymentEngine}
              selectedPaymentMethod={selectedPaymentMethod}
              fetchingPayment={fetchingPayment}
              isUpdating={isUpdating || Boolean(purchase.applyingDiscount)}
              variant="quickPurchase"
              collapsiblePurchaserForm
              purchaserFormDefaultExpanded={buyerFormDefaultExpanded}
              isOrchestrating={isOrchestrating}
              orchestrationError={orchestrationError}
              purchaserSubmitted={purchaserSubmitted}
              onPurchaserSubmitted={markPurchaserSaved}
              onBeforePaymentAsync={isEditingPassengers ? handleBeforePaymentAsync : null}
            />
          </Spacing>
        )}
      </div>
    </div>
  );
};

QuickCheckout.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  goToSearch: PropTypes.func.isRequired,
  orchestrationParams: PropTypes.shape({
    passengersParam: PropTypes.string,
    selectedSeatNumbers: PropTypes.arrayOf(PropTypes.string),
    selectedSeatReferences: PropTypes.arrayOf(PropTypes.object),
  }),
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  timerRemainingTime: PropTypes.number,
  onOrchestrationChange: PropTypes.func,
  onCouponAvailabilityChange: PropTypes.func,
};

QuickCheckout.defaultProps = {
  orchestrationParams: null,
  isModal: false,
  onClose: null,
  timerRemainingTime: 0,
  onOrchestrationChange: null,
  onCouponAvailabilityChange: null,
};

export default QuickCheckout;
